import { useEffect } from 'react';
import { QuizProvider, useQuiz } from './context/QuizContext';
import { HUD } from './components/HUD/HUD';
import { ErrorPortal } from './components/ErrorPortal/ErrorPortal';
import { QuestionScreen } from './screens/QuestionScreen';
import { MinigameScreen } from './screens/MinigameScreen';
import { EstatisticasScreen } from './screens/EstatisticasScreen';
import { FaseSelectScreen } from './screens/FaseSelectScreen';
import { TendaScreen } from './screens/TendaScreen';
import { ConquistasScreen } from './screens/ConquistasScreen';
import { TutorialScreen } from './screens/TutorialScreen';
import { GameOverScreen } from './screens/GameOverScreen';
import { fetchPerguntasByFase } from './services/quizFirebaseService';
import { getPersonagemPorFase } from './services/personagemService';
import { buildRetratoUrl, preloadImage } from './utils/assetUrl';
import { CharacterPortrait } from './components/CharacterPortrait/CharacterPortrait';
import { isActiveRunStatus } from './utils/quizUtils';
import { OverlayFeedback } from './components/OverlayFeedback';
import { FeedbackToast } from './components/FeedbackToast';
import { useGameAudio } from './hooks/useGameAudio';
import { ShieldAlert, Volume2, VolumeX, BarChart3, Map as MapIcon, ShoppingBag, Award, Gift, Play, Globe } from 'lucide-react';
import { GameShell } from './components/layout/GameShell';
import { StageAmbience } from './components/layout/StageAmbience';
import { ConsentBanner } from './components/ConsentBanner/ConsentBanner';

const BASE_URL = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';

const EngineController = () => {
  const { state, dispatch } = useQuiz();
  const { isMuted, toggleMute, playHover, playClick } = useGameAudio();

  const startFase = async (fase: number) => {
    // Dispara o download do retrato em paralelo com a busca das perguntas: quando a
    // QuestionScreen montar, a imagem já está no cache e não entra no caminho do LCP.
    // Sem await de propósito — nunca deve atrasar o início da fase.
    preloadImage(buildRetratoUrl(getPersonagemPorFase(fase)?.imagem_slug));

    try {
      const perguntas = await fetchPerguntasByFase(fase);
      dispatch({ type: 'START_PHASE', payload: { perguntas, fase } });
    } catch (e) {
      console.error(e);
      dispatch({
        type: 'SHOW_FEEDBACK',
        payload: { message: "Erro ao conectar com servidores. Tente através de Selecionar Fase.", type: 'error' }
      });
    }
  };

  useEffect(() => {
    if (!state.feedbackMessage) return;
    const id = setTimeout(() => {
      dispatch({ type: 'CLEAR_FEEDBACK' });
    }, 3000);
    return () => clearTimeout(id);
  }, [state.feedbackMessage, dispatch]);

  return (
    <div className="h-dvh max-h-dvh flex flex-col relative bg-transparent overflow-hidden">

      {/* Moldura viva: mundo de jogo atrás do palco (o que sobra vira atmosfera,
          não vazio). Cor acompanha o ciclo da fase ativa. */}
      <StageAmbience fase={state.faseAtual} />

      {/* Consentimento LGPD — controla o carregamento do Google Analytics */}
      <ConsentBanner />

      <HUD />

      <OverlayFeedback
        visible={state.overlayFeedback.visible}
        result={state.overlayFeedback.result}
        context={state.overlayFeedback.context}
        message={state.overlayFeedback.message}
        onClose={() => dispatch({ type: 'HIDE_OVERLAY_FEEDBACK' })}
      />

      <FeedbackToast
        visible={!!state.feedbackMessage && !state.overlayFeedback.visible}
        message={state.feedbackMessage}
        type={state.feedbackType}
      />

      <main className="flex-1 h-full flex flex-col overflow-hidden relative">
        {state.status === 'MENU' && (
          <GameShell maxWidth="sm" maxHeightCeiling="max-h-[100dvh]" className="text-center justify-between">
            {/* Top Bar: Mute toggle + Exit to Winiverso + Logo Banner */}
            <div className="w-full flex flex-col items-center shrink-0 pt-1">
              <div className="w-full flex justify-between items-center mb-1">
                <a
                  href="https://www.winiverso.com/"
                  className="px-2.5 py-1.5 rounded-xl glass-panel text-slate-300 hover:text-white hover:bg-sky-500/20 transition border border-slate-700 hover:border-sky-400 font-cyber text-fluid-xs font-semibold flex items-center gap-1.5"
                  title="Sair para o portal Winiverso"
                >
                  <Globe size={14} className="text-sky-400" />
                  <span>Winiverso</span>
                </a>

                <button
                  onClick={() => { playClick(); toggleMute(); }}
                  className="p-2 rounded-xl glass-panel text-slate-400 hover:text-white transition shadow-neon-cyan relative after:absolute after:-inset-1 after:content-['']"
                  title="Alternar Som"
                >
                  {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
              </div>
              <ShieldAlert size={36} className="text-amber-500 animate-[glow-pulse_2s_infinite] mb-1 filter drop-shadow-[0_0_12px_rgba(245,158,11,0.6)]" strokeWidth={1.5} />
              <h1 className="text-fluid-hero font-cyber font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-400 text-center tracking-tighter leading-tight drop-shadow-md">
                QUIZ DOS IMORTAIS
              </h1>
              <p className="text-sky-400 tracking-widest text-[10px] sm:text-xs uppercase font-bold text-center mt-0.5">
                Legado & Sobrevivência
              </p>
            </div>

            {/* Middle Stage: Showcase do Imortal + status da jornada. O painel
                preenche com dados reais (L5 opção 4) o que antes eram duas
                bandas mortas de 77–198px em volta do retrato. */}
            <div className="flex-1 min-h-0 w-full flex flex-col items-center justify-center py-2">
              <div className="stage-module items-center w-full">
                <CharacterPortrait fase={state.faseAtual} variant="hero" eager className="scale-95 sm:scale-100" />
                <div className="glass-panel rounded-xl border-slate-700/60 w-full max-w-[270px] min-h-[clamp(2.25rem,6dvh,3.5rem)] px-3 py-1.5 flex items-center justify-around gap-2">
                  <div className="flex flex-col items-center leading-tight">
                    <span className="text-[9px] text-slate-400 font-cyber uppercase tracking-widest">Jornada</span>
                    <span className="font-cyber font-bold text-sky-300 text-xs sm:text-sm">{state.fasesConcluidas.length}/50</span>
                  </div>
                  <div className="h-6 w-px bg-slate-700/70"></div>
                  <div className="flex flex-col items-center leading-tight">
                    <span className="text-[9px] text-slate-400 font-cyber uppercase tracking-widest">Axé</span>
                    <span className="font-cyber font-bold text-amber-400 text-xs sm:text-sm">{state.moedas}</span>
                  </div>
                  <div className="h-6 w-px bg-slate-700/70"></div>
                  <div className="flex flex-col items-center leading-tight">
                    <span className="text-[9px] text-slate-400 font-cyber uppercase tracking-widest">Búzios</span>
                    <span className="font-cyber font-bold text-red-300 text-xs sm:text-sm">{state.vidas}/{state.maxVidas}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Anchor: Grid de Botões de Ação na Zona do Polegar */}
            <div className="w-full flex flex-col gap-2 shrink-0 pb-1">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onMouseEnter={playHover}
                  onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'ESTATISTICAS' }); }}
                  className="btn-cyber py-2.5 px-3 rounded-xl flex items-center justify-between bg-slate-900/60 border border-slate-700/80 hover:border-sky-500/50 hover:shadow-neon-cyan transition-all group"
                >
                  <span className="font-cyber font-bold text-fluid-sm tracking-wider text-sky-100 group-hover:text-white">Desempenho</span>
                  <BarChart3 size={15} className="text-sky-400" />
                </button>

                <button
                  onMouseEnter={playHover}
                  onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'FASE_SELECT' }); }}
                  className="btn-cyber py-2.5 px-3 rounded-xl flex items-center justify-between bg-slate-900/60 border border-slate-700/80 hover:border-amber-500/50 hover:shadow-neon-amber transition-all group"
                >
                  <span className="font-cyber font-bold text-fluid-sm tracking-wider text-slate-200 group-hover:text-white">Fases</span>
                  <MapIcon size={15} className="text-amber-400" />
                </button>

                <button
                  onMouseEnter={playHover}
                  onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'TENDA' }); }}
                  className="btn-cyber py-2.5 px-3 rounded-xl flex items-center justify-between bg-slate-900/60 border border-slate-700/80 hover:border-emerald-500/50 hover:shadow-[0_0_12px_rgba(16,185,129,0.3)] transition-all group"
                >
                  <span className="font-cyber font-bold text-fluid-sm tracking-wider text-slate-200 group-hover:text-white">Tenda</span>
                  <ShoppingBag size={15} className="text-emerald-400" />
                </button>

                <button
                  onMouseEnter={playHover}
                  onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'CONQUISTAS' }); }}
                  className="btn-cyber py-2.5 px-3 rounded-xl flex items-center justify-between bg-slate-900/60 border border-slate-700/80 hover:border-purple-500/50 hover:shadow-[0_0_12px_rgba(168,85,247,0.3)] transition-all group"
                >
                  <span className="font-cyber font-bold text-fluid-sm tracking-wider text-slate-200 group-hover:text-white">Conquistas</span>
                  <Award size={15} className="text-purple-400" />
                </button>
              </div>

              {state.ultimoBonusDiario == null || Date.now() - state.ultimoBonusDiario > 24 * 60 * 60 * 1000 ? (
                <button
                  onMouseEnter={playHover}
                  onClick={() => { playClick(); dispatch({ type: 'CLAIM_BONUS_DIARIO', payload: 50 }); }}
                  className="btn-cyber w-full py-2 rounded-xl bg-emerald-600/20 border border-emerald-500/50 text-emerald-400 font-cyber font-bold tracking-wider hover:bg-emerald-600/40 transition uppercase text-fluid-sm flex items-center justify-center gap-2"
                >
                  <Gift size={14} /> Resgate Olorum (+50 Axé)
                </button>
              ) : null}

              <button
                onMouseEnter={playHover}
                onClick={() => { playClick(); startFase(1); }}
                className="btn-cyber w-full py-3 rounded-xl bg-emerald-600 border-2 border-emerald-400 text-white font-cyber font-black tracking-widest text-fluid-subtitle shadow-neon-emerald hover:bg-emerald-500 transition-colors uppercase flex items-center justify-center gap-2"
              >
                <Play size={18} fill="currentColor" /> Iniciar Sessão
              </button>

              <div className="shrink-0 pt-0.5 flex flex-col items-center gap-0.5">
                <div className="text-[9px] text-slate-500 tracking-widest uppercase font-cyber">
                  Quiz dos Imortais • v0.1.0 AAA+
                </div>
                <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-cyber uppercase tracking-wider">
                  <a href={`${BASE_URL}legal/termos-de-uso.html`} target="_blank" rel="noopener noreferrer" className="hover:text-sky-400 transition">Termos</a>
                  <span className="text-slate-700">·</span>
                  <a href={`${BASE_URL}legal/politica-de-privacidade.html`} target="_blank" rel="noopener noreferrer" className="hover:text-sky-400 transition">Privacidade</a>
                  <span className="text-slate-700">·</span>
                  <a href={`${BASE_URL}legal/politica-de-cookies.html`} target="_blank" rel="noopener noreferrer" className="hover:text-sky-400 transition">Cookies</a>
                </div>
              </div>
            </div>
          </GameShell>
        )}

        {state.status === 'ESTATISTICAS' && <EstatisticasScreen />}
        {state.status === 'FASE_SELECT' && <FaseSelectScreen />}
        {state.status === 'TENDA' && <TendaScreen />}
        {state.status === 'CONQUISTAS' && <ConquistasScreen />}
        {state.status === 'TUTORIAL' && <TutorialScreen />}

        {isActiveRunStatus(state.status) && state.perguntasFase.length === 0 && (
          <div className="m-auto text-center p-6 text-slate-400 font-cyber tracking-widest animate-pulse flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-sky-400 border-t-transparent animate-spin"></div>
            <span>Restaurando jornada...</span>
          </div>
        )}
        {state.status === 'PLAYING' && state.perguntasFase.length > 0 && <QuestionScreen />}
        {state.status === 'ERROR_PORTAL' && state.perguntasFase.length > 0 && (
          state.perguntaErro ? <ErrorPortal /> : <QuestionScreen />
        )}
        {state.status === 'MINIGAME' && state.perguntasFase.length > 0 && (
          state.desafioAtual ? <MinigameScreen /> : <QuestionScreen />
        )}

        {state.status === 'VICTORY' && (
          <GameShell maxWidth="sm" maxHeightCeiling="max-h-[min(100%,32rem)]" center>
            <div className="text-center p-5 glass-panel rounded-2xl border-emerald-500/40 animate-in zoom-in-95 duration-300 flex flex-col items-center my-auto">
              <CharacterPortrait fase={state.faseAtual} variant="hero" mood="success" eager className="mb-2 shrink-0" />
              <h2 className="text-fluid-title font-bold font-cyber text-emerald-400 mb-1">Fase Concluída!</h2>
              <p className="text-slate-300 text-fluid-body mb-1">Você acumulou <strong className="text-amber-400">{state.pontuacaoFase} Axé</strong> nesta fase.</p>
              {state.ajudasUsadasNestaFase === 0 && (
                <p className="text-emerald-400 text-fluid-sm mb-3 font-medium">Bônus Mestre sem ajudas: +200 Axé.</p>
              )}
              <div className="flex flex-col gap-2 mt-2 w-full">
                <button onClick={() => dispatch({ type: 'NAVIGATE', payload: 'FASE_SELECT' })} className="btn-cyber bg-slate-800 text-white py-2.5 rounded-xl font-bold w-full hover:bg-slate-700 transition text-fluid-body">
                  Selecionar outra fase
                </button>
                <button onClick={() => startFase(state.faseAtual)} className="btn-cyber bg-amber-500 text-slate-950 py-2.5 rounded-xl font-bold w-full hover:bg-amber-400 transition text-fluid-body">
                  Jogar novamente
                </button>
              </div>
            </div>
          </GameShell>
        )}

        {state.status === 'VICTORY_FASE_50' && (
          <GameShell maxWidth="sm" maxHeightCeiling="max-h-[min(100%,34rem)]" center>
            <div className="text-center p-5 glass-panel rounded-2xl border-amber-500/50 animate-in zoom-in-95 duration-300 flex flex-col items-center my-auto">
              <CharacterPortrait fase={50} variant="hero" showLabel={false} eager className="mb-2 shrink-0" />
              <h2 className="text-fluid-title font-bold font-cyber text-amber-400 mb-1">Tribunal dos Antepassados</h2>
              <p className="text-white font-bold text-fluid-subtitle mb-1">Ancestral da Sabedoria</p>
              <p className="text-slate-300 text-fluid-sm mb-3">Seu nome será eternizado no Mural de Ouro da Biografia Preta.</p>
              <p className="text-emerald-400 font-bold mb-4">+{state.pontuacaoFase} Axé nesta fase.</p>
              <button onClick={() => dispatch({ type: 'NAVIGATE', payload: 'FASE_SELECT' })} className="btn-cyber bg-amber-500 text-slate-950 py-2.5 rounded-xl font-bold w-full hover:bg-amber-400 transition text-fluid-body">
                Voltar ao mapa
              </button>
            </div>
          </GameShell>
        )}

        {state.status === 'GAME_OVER' && <GameOverScreen />}

        {state.status === 'DUELO_ACCEPT' && (
          <GameShell maxWidth="sm" maxHeightCeiling="max-h-[min(100%,24rem)]" center>
            <div className="text-center p-6 glass-panel rounded-2xl my-auto">
              <h2 className="text-fluid-title font-bold text-white mb-2">Duelo Social</h2>
              <p className="text-slate-400 text-fluid-body mb-4">Desafio em desenvolvimento.</p>
              <button onClick={() => dispatch({ type: 'NAVIGATE', payload: 'MENU' })} className="btn-cyber bg-slate-700 text-white px-6 py-2.5 rounded-xl text-fluid-body font-medium hover:bg-slate-600">
                Voltar ao Menu
              </button>
            </div>
          </GameShell>
        )}
      </main>
    </div>
  );
};

export default function App() {
  return (
    <QuizProvider>
      <EngineController />
    </QuizProvider>
  );
}
